import { CountryCode, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { stringNotEmpty } from 'Util/TypeGuards';

import { UnitLabels } from 'Util/UnitOfMeasurementUtils';
import { DEFAULT_COUNTRY_CODE } from 'Constants';
import { ConfigGeneralEntity, IConfigGeneralEntityAttributes } from './Entities';

export class WhiteLabelStore {
	config: IConfigGeneralEntityAttributes;

	constructor(config: IConfigGeneralEntityAttributes[]) {
		if (!config) {
			return;
		}
		this.config = config?.find(x => x.organisation === null) ?? config[0];
	}

	public setConfig(config: IConfigGeneralEntityAttributes | ConfigGeneralEntity | undefined) {
		if (config) {
			this.config = config;
			// makeAutoObservable(this); // Makes the store reactive
		}
	}

	public get defaultRouteId() {
		return this.config.defaultRouteId;
	}

	public get GetVehicleDefaultTripType() {
		return (this.config.vehicleDefaultToReturn ?? true) ? 'return' : 'one way';
	}

	public get GetPassengerDefaultTripType() {
		return (this.config.passengersDefaultToReturn ?? true) ? 'return' : 'one way';
	}

	public get defaultBookingType() {
		if (this.config.defaultBookingType === 'CARGO') {
			return 'vehicle';
		}
		if (this.config.defaultBookingType === 'FREIGHT') {
			return 'freight';
		}
		return 'passenger';
	}

	public get generalTerms() {
		return this.config.generalTerms;
	}

	public get vehicleTerms() {
		return this.config.vehicleTerms;
	}

	public get passengerTerms() {
		return this.config.passengerTerms;
	}

	public get freightTerms() {
		return this.config.freightTerms;
	}

	public get eventTerms() {
		return this.config.defaultEventTerms;
	}

	public get countryCode() {
		const validCountryCodes = new Set(getCountries());

		if (validCountryCodes.has(this.config.defaultNumberFormattingCode as any)) {
			return this.config.defaultNumberFormattingCode as CountryCode;
		}
		return DEFAULT_COUNTRY_CODE;
	}

	public get defaultInternationalPhonePrefix() {
		return `+${getCountryCallingCode(this.countryCode)}`;
	}

	public get systemMeasurementUnit() {
		return this.config.systemOfMeasurement;
	}

	public get volumeUnitLabel() {
		if (this.config.systemOfMeasurement === 'IMPERIAL') {
			return UnitLabels.volume.cubicFeet;
		}
		return UnitLabels.volume.cubicMeters;
	}

	public get massUnitLabel() {
		if (this.config.systemOfMeasurement === 'IMPERIAL') {
			return UnitLabels.mass.pounds;
		}
		return UnitLabels.mass.kilograms;
	}

	public get dimensionUnitLabel() {
		if (this.config.systemOfMeasurement === 'IMPERIAL') {
			return UnitLabels.length.inches;
		}
		return UnitLabels.length.millimeters;
	}

	public get dateFormat() {
		if (
			stringNotEmpty(this.config.dateFormat) &&
			(this.config.dateFormat.toLowerCase() === 'dd/mm/yyyy' ||
				this.config.dateFormat.toLowerCase() === 'mm/dd/yyyy' ||
				this.config.dateFormat.toLowerCase() === 'yyyy/mm/dd' ||
				this.config.dateFormat.toLowerCase() === 'yyyy/dd/mm')
		) {
			return this.config.dateFormat.toUpperCase();
		}
		return 'DD/MM/YYYY';
	}

	public get datePickerDateFormat() {
		const sym = '/';
		if (stringNotEmpty(this.config.dateFormat)) {
			if (this.config.dateFormat.toLowerCase() === 'mm/dd/yyyy') {
				return `m${sym}d${sym}Y`;
			}
			if (this.config.dateFormat.toLowerCase() === 'yyyy/mm/dd') {
				return `Y${sym}m${sym}d`;
			}
			if (this.config.dateFormat.toLowerCase() === 'yyyy/dd/mm') {
				return `Y${sym}d${sym}m`;
			}
		}

		// Default
		return `d${sym}m${sym}Y`;
	}
}

export const whiteLabelStore = new WhiteLabelStore(window.whitelabelData);
